const HCAPTCHA_SCRIPT_ID = 'autocx-hcaptcha-script';
const HCAPTCHA_SCRIPT_SRC = 'https://js.hcaptcha.com/1/api.js?render=explicit';

let scriptPromise = null;

const normaliseSiteKey = (siteKey) => {
    if (typeof siteKey !== 'string') return '';
    return siteKey.trim();
};

const ensureHiddenInput = (formElement, name) => {
    if (!formElement) return null;

    let input = formElement.querySelector(`input[name="${name}"]`);
    if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        formElement.appendChild(input);
    }

    return input;
};

const setInputValue = (input, value) => {
    if (!input) return;
    input.value = value || '';
};

const loadHCaptchaScript = () => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return Promise.resolve(null);
    }

    if (window.hcaptcha?.render) {
        return Promise.resolve(window.hcaptcha);
    }

    if (scriptPromise) {
        return scriptPromise;
    }

    scriptPromise = new Promise((resolve, reject) => {
        const existingScript = document.getElementById(HCAPTCHA_SCRIPT_ID);
        if (existingScript) {
            existingScript.addEventListener('load', () => resolve(window.hcaptcha), {once: true});
            existingScript.addEventListener('error', () => reject(new Error('Failed to load hCaptcha script')), {once: true});
            return;
        }

        const script = document.createElement('script');
        script.id = HCAPTCHA_SCRIPT_ID;
        script.src = HCAPTCHA_SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve(window.hcaptcha);
        script.onerror = () => reject(new Error('Failed to load hCaptcha script'));
        document.head.appendChild(script);
    }).catch((error) => {
        scriptPromise = null;
        throw error;
    });

    return scriptPromise;
};

const createHCaptchaController = ({formElement, containerElement, siteKey, onStateChange} = {}) => {
    const resolvedSiteKey = normaliseSiteKey(siteKey);

    const providerTokenInput = ensureHiddenInput(formElement, 'h-captcha-response');
    const tokenInput = ensureHiddenInput(formElement, 'captchaToken');
    const statusInput = ensureHiddenInput(formElement, 'captchaStatus');

    let widgetId = null;
    let destroyed = false;
    let loadFailed = false;
    let currentToken = '';
    let currentStatus = '';
    let executionPromise = null;
    let executionResolver = null;

    const emitStateChange = () => {
        if (typeof onStateChange === 'function') {
            onStateChange({
                token: currentToken,
                status: currentStatus,
                ready: widgetId !== null,
                loadFailed,
            });
        }
    };

    const syncState = ({token = currentToken, status = currentStatus} = {}) => {
        currentToken = token || '';
        currentStatus = status || '';
        setInputValue(providerTokenInput, currentToken);
        setInputValue(tokenInput, currentToken);
        setInputValue(statusInput, currentStatus);
        if (currentToken && executionResolver) {
            executionResolver(currentToken);
            executionResolver = null;
            executionPromise = null;
        }
        emitStateChange();
    };

    const clearState = (status = '') => {
        if (executionResolver && status && status !== 'valid') {
            executionResolver(null);
            executionResolver = null;
            executionPromise = null;
        }
        syncState({token: '', status});
    };

    const renderWidget = async () => {
        if (destroyed || !formElement || !containerElement || !resolvedSiteKey) {
            clearState();
            return false;
        }

        try {
            const hcaptcha = await loadHCaptchaScript();
            if (destroyed || !hcaptcha?.render) {
                return false;
            }

            if (widgetId !== null) {
                return true;
            }

            widgetId = hcaptcha.render(containerElement, {
                sitekey: resolvedSiteKey,
                size: 'invisible',
                callback: (token) => {
                    syncState({token, status: token ? 'valid' : ''});
                },
                'expired-callback': () => {
                    clearState('expired');
                },
                'error-callback': () => {
                    clearState('error');
                },
            });

            emitStateChange();
            return true;
        } catch (error) {
            loadFailed = true;
            emitStateChange();
            return false;
        }
    };

    return {
        init: () => renderWidget(),
        execute: async () => {
            if (!resolvedSiteKey) return null;
            if (currentToken) return currentToken;

            const ready = await renderWidget();
            if (!ready || destroyed || typeof window === 'undefined' || !window.hcaptcha || widgetId === null) {
                return null;
            }

            if (executionPromise) {
                return executionPromise;
            }

            executionPromise = new Promise((resolve) => {
                executionResolver = resolve;
                try {
                    window.hcaptcha.execute(widgetId);
                } catch (error) {
                    executionResolver = null;
                    executionPromise = null;
                    resolve(null);
                }
            });

            return executionPromise;
        },
        reset: () => {
            if (typeof window !== 'undefined' && window.hcaptcha && widgetId !== null) {
                window.hcaptcha.reset(widgetId);
            }

            clearState();
        },
        destroy: () => {
            destroyed = true;
            if (typeof window !== 'undefined' && window.hcaptcha && widgetId !== null && typeof window.hcaptcha.remove === 'function') {
                window.hcaptcha.remove(widgetId);
            }

            widgetId = null;
            setInputValue(providerTokenInput, '');
            setInputValue(tokenInput, '');
            setInputValue(statusInput, '');
            currentToken = '';
            currentStatus = '';
        },
        getToken: () => currentToken,
        getStatus: () => currentStatus,
        isConfigured: () => resolvedSiteKey.length > 0,
        isReady: () => widgetId !== null,
        hasLoadFailure: () => loadFailed,
    };
};

module.exports = {
    createHCaptchaController,
    loadHCaptchaScript,
};
module.exports.default = module.exports;
